import React, { useEffect, useState } from 'react';
import { Download, Share, X, PlusSquare } from 'lucide-react';

const DISMISSED_KEY = 'install_prompt_dismissed';

// beforeinstallprompt can fire before this component mounts (e.g. during the
// auth check), so capture it as soon as the module loads
let earlyPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  earlyPrompt = e;
});

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function isIos() {
  const ua = window.navigator.userAgent;
  // iPadOS reports itself as Mac; touch support tells them apart
  return /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

function wasDismissed() {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Suggests installing the app (mobile only): the native install prompt on
 * Android/Chrome, a one-time "Share → Add to Home Screen" hint on iOS.
 * Dismissing hides it for good.
 */
function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(() => earlyPrompt);
  const [showIosHint, setShowIosHint] = useState(false);
  const [hidden, setHidden] = useState(() => isStandalone() || wasDismissed());

  useEffect(() => {
    if (hidden) return undefined;
    const onBeforeInstall = (e) => {
      e.preventDefault();
      earlyPrompt = e;
      setDeferredPrompt(e);
    };
    const onInstalled = () => setHidden(true);
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    if (isIos()) setShowIosHint(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [hidden]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch { /* ignore */ }
    setHidden(true);
  };

  const install = async () => {
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    earlyPrompt = null;
    if (outcome === 'accepted') setHidden(true);
    else dismiss();
  };

  if (hidden || (!deferredPrompt && !showIosHint)) return null;

  return (
    <div className="md:hidden mb-4 rounded-2xl bg-blue-600 text-white p-4 shadow-lg">
      <div className="flex items-start gap-3">
        <Download className="w-6 h-6 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold">Install Expense Tracker</p>
          {deferredPrompt ? (
            <p className="text-sm text-blue-100 mt-0.5">Faster camera access and receipt capture without a connection.</p>
          ) : (
            <p className="text-sm text-blue-100 mt-0.5">
              Tap <Share className="inline w-4 h-4 -mt-0.5" /> <strong>Share</strong>, then{' '}
              <PlusSquare className="inline w-4 h-4 -mt-0.5" /> <strong>Add to Home Screen</strong>.
            </p>
          )}
          {deferredPrompt && (
            <button
              type="button"
              onClick={install}
              className="mt-3 px-4 py-2 rounded-xl bg-white text-blue-700 text-sm font-semibold active:bg-blue-50"
            >
              Install app
            </button>
          )}
        </div>
        <button type="button" onClick={dismiss} className="-mr-1 -mt-1 p-1.5 rounded-full text-blue-100 active:bg-blue-500" aria-label="Dismiss">
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export default InstallPrompt;
